import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Edit,
  Trash2,
  CheckCircle,
  Truck,
  MapPin,
  Calendar,
  ShoppingCart,
  Receipt,
  Printer,
  XCircle,
  Ban,
} from 'lucide-react';
import { Button } from '@components/Button';
import { Card, CardHeader, CardContent, Badge } from '@components/Card';
import { ConfirmDialog } from '@components/Dialog';
import { PermissionGuard } from '@components/PermissionGuard';
import { CancelOrderDialog } from '@components/CancelOrderDialog';
import { formatDateBR, formatDateTimeBR } from '@pages/financial/financialUtils';
import { goodsPickupOrderService } from '../../services/goodsPickupOrderService';
import { GoodsPickupOrder } from '../../types/goodsPickupOrder';
import { PICKUP_ORDER_STATUS_LABELS, pickupOrderStatus, pickupOrderStatusBadge } from './goodsPickupOrderUtils';

export const GoodsPickupOrderDetailView: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [order, setOrder] = useState<GoodsPickupOrder | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [showCancelDialog, setShowCancelDialog] = useState(false);

  useEffect(() => {
    if (id) loadOrder(parseInt(id));
  }, [id]);

  const loadOrder = async (orderId: number) => {
    try {
      setLoading(true);
      const data = await goodsPickupOrderService.getOrderById(orderId);
      setOrder(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar ordem de retirada');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!order) return;
    try {
      await goodsPickupOrderService.deleteOrder(order.goods_pickup_orders_id);
      navigate('/goods-pickup-orders');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao excluir');
    }
  };

  const handleMarkDelivered = async () => {
    if (!order) return;
    try {
      const updated = await goodsPickupOrderService.markAsDelivered(order.goods_pickup_orders_id);
      setOrder(updated);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao marcar como entregue');
    }
  };

  const handlePrintPDF = async () => {
    if (!order) return;
    setPdfLoading(true);
    try {
      const blob = await goodsPickupOrderService.downloadPdf(order.goods_pickup_orders_id);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao gerar PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const handleCancel = async (reason: string) => {
    if (!order) return;
    const updated = await goodsPickupOrderService.cancelOrder(order.goods_pickup_orders_id, reason);
    setOrder(updated);
    setShowCancelDialog(false);
  };

  // Datas da API em wall-clock BR: formatar pelo texto, sem passar por Date.
  const formatDate = (dateStr?: string | null) => formatDateBR(dateStr) || '—';

  const formatDateTime = (dateStr?: string | null) => formatDateTimeBR(dateStr) || '—';

  const formatCep = (cep?: string) => {
    if (!cep) return '—';
    const c = cep.replace(/\D/g, '');
    return c.length === 8 ? `${c.slice(0, 5)}-${c.slice(5)}` : cep;
  };

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4" />
        <p className="text-slate-500">Carregando...</p>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="text-center py-12">
        <Truck className="w-12 h-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 mb-4">{error || 'Ordem não encontrada'}</p>
        <Button variant="outline" onClick={() => navigate('/goods-pickup-orders')}>
          Voltar
        </Button>
      </div>
    );
  }

  const status = pickupOrderStatus(order);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            icon={<ArrowLeft size={20} />}
            onClick={() => navigate('/goods-pickup-orders')}
          >
            Voltar
          </Button>
          <div>
            <h1 className="text-h1">Ordem de Retirada #{order.goods_pickup_orders_id}</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant={pickupOrderStatusBadge(status)}>
                {PICKUP_ORDER_STATUS_LABELS[status]}
              </Badge>
              {order.is_delivered && order.delivered_at && (
                <span className="text-sm text-slate-500">
                  Entregue em {formatDateTime(order.delivered_at)}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <PermissionGuard permission="goods_pickup_orders.read">
            <Button
              variant="outline"
              icon={pdfLoading ? undefined : <Printer size={18} />}
              onClick={handlePrintPDF}
              disabled={pdfLoading}
            >
              {pdfLoading ? 'Gerando PDF...' : 'Imprimir'}
            </Button>
          </PermissionGuard>

          {status === 'pending' && (
            <>
              <PermissionGuard permission="goods_pickup_orders.write">
                <Button
                  variant="outline"
                  icon={<XCircle size={18} />}
                  onClick={() => setShowCancelDialog(true)}
                  className="text-amber-600"
                >
                  Cancelar ordem
                </Button>
              </PermissionGuard>

              <PermissionGuard permission="goods_pickup_orders.write">
                <Button
                  variant="outline"
                  icon={<CheckCircle size={18} />}
                  onClick={handleMarkDelivered}
                  className="text-green-600"
                >
                  Marcar Entregue
                </Button>
              </PermissionGuard>

              <PermissionGuard permission="goods_pickup_orders.write">
                <Button
                  variant="outline"
                  icon={<Edit size={18} />}
                  onClick={() => navigate(`/goods-pickup-orders/${order.goods_pickup_orders_id}/edit`)}
                >
                  Editar
                </Button>
              </PermissionGuard>

              <PermissionGuard permission="goods_pickup_orders.delete">
                <Button
                  variant="outline"
                  icon={<Trash2 size={18} />}
                  onClick={() => setShowDeleteDialog(true)}
                  className="text-red-500"
                >
                  Excluir
                </Button>
              </PermissionGuard>
            </>
          )}
        </div>
      </div>

      {/* Cancelamento */}
      {status === 'canceled' && (
        <div className="flex items-start gap-3 rounded-lg border border-slate-300 bg-slate-100 px-4 py-3">
          <Ban size={20} className="mt-0.5 shrink-0 text-slate-600" />
          <div className="text-sm text-slate-700">
            <p className="font-semibold text-slate-900">Ordem cancelada</p>
            <p className="mt-1"><span className="font-medium">Motivo:</span> {order.cancel_reason || '—'}</p>
            <p className="mt-0.5 text-slate-600">
              Cancelada por {order.canceled_by || '—'} em {formatDateTime(order.canceled_at)}
            </p>
          </div>
        </div>
      )}

      {/* Descrição */}
      <Card>
        <CardHeader title="Dados da Retirada" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <p className="text-sm font-medium text-slate-500">Descrição</p>
              <p className="text-slate-900 mt-1">{order.description}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Contato para retirada</p>
              <p className="text-slate-900 mt-1">{order.pickup_contact_name || '—'}</p>
            </div>
            <div className="flex items-center gap-2">
              <Calendar size={16} className="text-slate-400" />
              <div>
                <p className="text-sm font-medium text-slate-500">Data limite</p>
                <p className="text-slate-900 mt-1">{formatDate(order.pickup_deadline_date)}</p>
              </div>
            </div>
            <div>
              <p className="text-sm font-medium text-slate-500">Quantidade de volumes</p>
              <p className="text-slate-900 mt-1">{order.volume_quantity ?? '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Vínculos */}
      {(order.provider_id || order.purchase_order_id || order.order_id) && (
        <Card>
          <CardHeader title="Vínculos" />
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {order.provider_id && (
                <div className="flex items-start gap-2">
                  <Truck size={16} className="text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">Fornecedor</p>
                    <p className="text-slate-900 mt-1">
                      {order.provider_name || `ID: ${order.provider_id}`}
                    </p>
                  </div>
                </div>
              )}
              {order.purchase_order_id && (
                <div className="flex items-start gap-2">
                  <ShoppingCart size={16} className="text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">Pedido de Compra</p>
                    <p className="text-slate-900 mt-1">PC #{order.purchase_order_id}</p>
                  </div>
                </div>
              )}
              {order.order_id && (
                <div className="flex items-start gap-2">
                  <Receipt size={16} className="text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-slate-500">Pedido de Venda</p>
                    <p className="text-slate-900 mt-1">PV #{order.order_id}</p>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Endereço */}
      <Card>
        <CardHeader title="Endereço de Retirada" />
        <CardContent>
          <div className="flex items-start gap-2">
            <MapPin size={16} className="text-slate-400 mt-0.5" />
            <div>
              <p className="text-slate-900">
                {order.ship_addr_street}, {order.ship_addr_num}
                {order.ship_addr_compl ? ` — ${order.ship_addr_compl}` : ''}
              </p>
              <p className="text-sm text-slate-600">
                {order.ship_addr_district} — {order.ship_addr_city}/{order.ship_addr_state}
              </p>
              <p className="text-sm text-slate-500">CEP: {formatCep(order.ship_addr_zip)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Itens */}
      {order.items && order.items.length > 0 && (
        <Card>
          <CardHeader title="Itens da Retirada" />
          <CardContent>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">SKU</th>
                    <th className="text-left px-4 py-2 text-xs font-medium text-slate-600">Descrição</th>
                    <th className="text-center px-4 py-2 text-xs font-medium text-slate-600">Quantidade</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {order.items.map((item, idx) => (
                    <tr key={idx}>
                      <td className="px-4 py-2">
                        <code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{item.sku}</code>
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-700">{item.description || '—'}</td>
                      <td className="px-4 py-2 text-center text-sm">{item.quantity}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Metadados */}
      <Card>
        <CardHeader title="Informações do Registro" />
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Solicitante</p>
              <p className="text-slate-900">{order.requestor_email || order.created_by || '—'}</p>
            </div>
            <div>
              <p className="text-slate-500">Criado em</p>
              <p className="text-slate-900">{formatDateTime(order.created_at)}</p>
            </div>
            <div>
              <p className="text-slate-500">Atualizado em</p>
              <p className="text-slate-900">{formatDateTime(order.updated_at)}</p>
            </div>
            <div>
              <p className="text-slate-500">Última atualização por</p>
              <p className="text-slate-900">{order.last_updated_by || '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDelete}
        title="Excluir Ordem de Retirada"
        message={`Deseja realmente excluir a ordem #${order.goods_pickup_orders_id}? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        severity="danger"
      />

      <CancelOrderDialog
        isOpen={showCancelDialog}
        onClose={() => setShowCancelDialog(false)}
        onConfirm={handleCancel}
        title="Cancelar Ordem de Retirada"
        subject={`a ordem de retirada #${order.goods_pickup_orders_id}`}
      />
    </div>
  );
};
